import { Client } from "@stomp/stompjs";
import { getCurrentScope, onScopeDispose } from "vue";
import { captureException } from "@sentry/vue";
import Swal from "sweetalert2";
import { useAppStore } from "~stores/App.ts";
import { useUserStore } from "~stores/User.ts";
import { refreshGridsOfResource, updateGridRows } from "~utils/Grids/RefreshGrid.ts";
import { currentSession, setActiveLanguage } from "@q-food/q-food";
import AppNotificationToastBody from "~components/AppNotificationToastBody.vue";
import { translate } from "~/plugins/I18n.ts";
import {
	appUpdate,
	Channel,
	gridRowUpdates,
	userNotifications,
	userOpenGrid,
	userRefreshApp,
	userRefreshCompanyActiveLanguage,
	userRefreshGrid,
	userRefreshUserActiveLanguage,
} from "~utils/SocketChannels.ts";

let socketUrl;
if (import.meta.env.VITE_QFOOD_WEBSOCKET_HOST == undefined || import.meta.env.VITE_QFOOD_WEBSOCKET_HOST == "") {
	socketUrl = `wss://${window.location.host}`;
} else {
	socketUrl = import.meta.env.VITE_QFOOD_WEBSOCKET_HOST;
}

const socketClient = new Client({
	brokerURL: `${socketUrl}/ws`,
	heartbeatOutgoing: 10_000,
	connectHeaders: {
		"x-auth-token": localStorage.getItem("qfood-session-token")!,
	},
});

const onConnectListeners = new Set<(client: Client) => void>();

/**
 * Run `func` on every (re)connect, and right away when the socket is already up.
 *
 * The listener is dropped again when the calling effect scope is disposed — a component unmounting,
 * a composable's scope ending. Without that it was a permanent registration: the list only ever grew,
 * so every closure a view ever passed stayed reachable, and with it that view's whole setup scope.
 * Worse, the next reconnect replayed those dead listeners, re-subscribing destroyed views whose own
 * cleanup (`useSocketSubscriber`'s `unsubscribeAll`, an `onCloseTab` handler) had already run and
 * would never run again — so their handlers kept firing for the rest of the session.
 *
 * Returns a manual off-switch for callers outside a scope.
 */
export function onConnect(func: (client: Client) => void): () => void {
	if (socketClient.active && socketClient.connected) {
		func(socketClient);
	}

	onConnectListeners.add(func);

	const off = () => {
		onConnectListeners.delete(func);
	};

	// Vue activates the component's scope for lifecycle hooks too, so this also covers the views that
	// call onConnect from inside onMounted.
	if (getCurrentScope()) {
		onScopeDispose(off);
	}

	return off;
}

function subscribeClient<T>(channel: Channel<T>, handler: (payload: T) => void) {
	socketClient.subscribe(channel.destination, (msg) => handler(channel.decode(msg.body)));
}

let backendUpdatePending = false;
let backendUpdateGraceTimeout: ReturnType<typeof setTimeout> | null = null;
let backendUpdatePingInterval: ReturnType<typeof setInterval> | null = null;
const BACKEND_UPDATE_PING_GRACE_MS = 30_000;
const BACKEND_UPDATE_PING_INTERVAL_MS = 10_000;

/** Close the update modal and tear down the grace/ping timers. Idempotent. */
function resolveBackendUpdate() {
	backendUpdatePending = false;
	if (backendUpdateGraceTimeout !== null) {
		clearTimeout(backendUpdateGraceTimeout);
		backendUpdateGraceTimeout = null;
	}
	if (backendUpdatePingInterval !== null) {
		clearInterval(backendUpdatePingInterval);
		backendUpdatePingInterval = null;
	}
	Swal.close();
}

/** GET the always-open actuator health endpoint. Resolves true only on a 2xx (backend UP). */
async function backendIsReachable(): Promise<boolean> {
	try {
		await currentSession();
		return true;
	} catch {
		return false;
	}
}

/** Show the blocking "backend is updating" modal and arm the reachability-ping fallback. */
function showBackendUpdatePopup() {
	if (backendUpdatePending) return;
	backendUpdatePending = true;

	Swal.fire({
		title: translate("common.messages.The application is being updated"),
		html: translate("common.messages.One moment, the application will shortly be available"),
		allowOutsideClick: false,
		allowEscapeKey: false,
		showConfirmButton: false,
		showCancelButton: false,
		heightAuto: false,
		didOpen: () => Swal.showLoading(),
	});

	// The socket reconnect (onConnect) is the primary way this modal closes. Only if the restart isn't
	// resolved within the grace window do we start polling the backend as a backstop.
	backendUpdateGraceTimeout = setTimeout(() => {
		backendUpdatePingInterval = setInterval(async () => {
			if (!(await backendIsReachable())) return;
			if (!socketClient.connected) {
				captureException(
					new Error(
						"Backend is reachable after an update but the STOMP socket never reconnected; closed the update modal via the reachability-ping fallback.",
					),
				);
			}
			resolveBackendUpdate();
		}, BACKEND_UPDATE_PING_INTERVAL_MS);
	}, BACKEND_UPDATE_PING_GRACE_MS);
}

socketClient.onConnect = (frame) => {
	const { toast } = useToast();
	if (backendUpdatePending) {
		resolveBackendUpdate();
	}

	onConnectListeners.forEach((listener) => listener(socketClient));

	subscribeClient(userNotifications, async (notification) => {
		const type = notification.type.toLowerCase();

		if (!(type in toast)) {
			return;
		}

		await new Promise((r) => setTimeout(r, 300));

		const userStore = useUserStore();
		await userStore.refreshNotifications();

		window.dispatchEvent(new CustomEvent("food:notification", { detail: notification }));

		const details: string[] = notification.details ?? [];

		// A notification that carries details is deliberately not marked as read when its toast is
		// dismissed (see onDismiss below), because the details still have to be read. Render the body as a
		// component in that case, so the details can be opened with an eye right here instead of only in
		// the notification dropdown. Only a non-string description is rendered as a component by
		// vue-sonner (its `action.label` is always coerced to text), and its props go through
		// `componentProps`.
		toast[type](notification.title, {
			...(details.length > 0
				? {
						description: AppNotificationToastBody,
						componentProps: { message: notification.message, details },
						// Long enough to actually open and read the details before it disappears.
						duration: 15000,
					}
				: { description: notification.message }),
			onDismiss: async () => {
				if (details.length === 0 && notification.priority === false) {
					await userStore.readNotification(notification);
				}
			},
		});
	});

	subscribeClient(userRefreshGrid, (resources) => {
		resources.forEach((resource) => refreshGridsOfResource(resource));
	});

	// Patch just the changed rows in place instead of refetching the whole grid. A client ignores any row
	// it doesn't have loaded and can only refetch through its own ACL-scoped endpoint, so a single global
	// topic is safe.
	subscribeClient(gridRowUpdates, ({ resource, rowIds }) => {
		updateGridRows(resource, rowIds);
	});

	// Assistant "open a grid" signal: { resource, criteria }. Handled in App.vue (needs router/setup context).
	subscribeClient(userOpenGrid, ({ resource, criteria }) => {
		window.dispatchEvent(new CustomEvent("food:grid:open", { detail: { resource, criteria } }));
	});

	subscribeClient(userRefreshApp, () => {
		useAppStore().refreshApp();
	});

	// Backend is shutting down for an update: show the blocking "please wait" modal + arm recovery.
	subscribeClient(appUpdate, () => {
		showBackendUpdatePopup();
	});

	subscribeClient(userRefreshUserActiveLanguage, async (languageCode) => {
		await setActiveLanguage(languageCode);
		useAppStore().refreshApp();
	});

	subscribeClient(userRefreshCompanyActiveLanguage, async (body) => {
		const [languageCode, companyId] = body.split(" ");
		const activeCompany = useUserStore().activeCompany;
		const activeLanguageCode = useUserStore().activeLanguageCode;
		if (activeCompany.id == companyId && activeLanguageCode != languageCode.toLowerCase()) {
			await setActiveLanguage(languageCode);
		}
		useAppStore().refreshApp();
	});
};

socketClient.onWebSocketClose = (event) => {
	if (event?.code === 1009) {
		console.log("STOMP socket closed (1009): frame too big for broker buffer", event.reason);
		captureException(new Error(`STOMP socket closed (1009): ${event.reason || "message too big"}`));
	}
};

// Dev only: the replaced module's STOMP client would otherwise keep its socket, its heartbeat timers
// and its reconnect loop running for the rest of the page's life.
if (import.meta.hot) {
	import.meta.hot.dispose(() => {
		onConnectListeners.clear();
		void socketClient.deactivate();
	});
}

export { socketClient };
